import React, { useMemo } from 'react'
import moment from 'moment'
import DashboardEditableRow from '@components/pages/Dashboard/DashboardEditableRow.component'
import { DASHBOARD_DETAIL_HREFS } from '@constants/Dashboard/dashboardDetailRoutes.constants'
import {
  buildDashboardDetailHref,
  DASHBOARD_DETAIL_CONTEXT,
} from '@constants/Dashboard/dashboardDetailContext.util'
import { HUDDLE_DEFAULT_HIDDEN_SECTIONS } from '@constants/Dashboard/huddleSectionVisibility.constants'
import {
  DashboardSection,
  HuddleDay,
} from '@constants/Dashboard/dashboard.enums'
import {
  EventsCard,
  FollowUpsFromYesterdayCard,
  GoalsForTomorrowCard,
  LeaderboardCard,
  OverdueActionsCard,
  PatientsCard,
  PerformanceVsGoalCard,
  PreparationForTomorrowCard,
  ProviderActualsYesterdayCard,
  ProviderPerformanceBreakdownCard,
  ScheduleAdjustmentsCard,
  ScheduleAtAGlanceCard,
  TodaysForecastCard,
  TomorrowsForecastCard,
  YesterdayActualsCard,
  YesterdaysPerformanceCard,
} from './SectionCards'
import { useHuddleDashboard, type HuddleDayTab } from './useHuddleDashboard'
import '@components/pages/Dashboard/DashboardLayout.style.css'
import './DashboardHuddle.style.css'

export type { HuddleDayTab } from './useHuddleDashboard'

type DashboardHuddleProps = {
  activeDayTab: HuddleDayTab
  editMode?: boolean
  hiddenSections?: Set<string>
}

function DashboardHuddle({
  activeDayTab,
  editMode = false,
  hiddenSections = new Set(),
}: DashboardHuddleProps): React.ReactElement {
  const show = (section: DashboardSection): boolean =>
    !HUDDLE_DEFAULT_HIDDEN_SECTIONS.has(section) && !hiddenSections.has(section)
  const todayDate = useMemo(() => moment().format('MMMM D, YYYY'), [])
  const yesterdayDate = useMemo(
    () => moment().subtract(1, 'day').format('MMMM D, YYYY'),
    [],
  )
  const tomorrowDate = useMemo(
    () => moment().add(1, 'day').format('MMMM D, YYYY'),
    [],
  )

  const huddle = useHuddleDashboard({
    activeDayTab,
    todayDate,
    yesterdayDate,
    tomorrowDate,
    hiddenSections,
  })
  const huddleDetailHref = useMemo(
    () =>
      (path: string): string =>
        buildDashboardDetailHref(path, {
          context: DASHBOARD_DETAIL_CONTEXT.huddle,
          day: activeDayTab,
        }),
    [activeDayTab],
  )

  return (
    <div className="dashboard-huddle">
      {activeDayTab === HuddleDay.Today && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-huddle-two-cards-row dashboard-huddle-top-row"
        >
          {show(DashboardSection.TodaysForecast) && (
            <TodaysForecastCard
              titleHref={huddleDetailHref(DASHBOARD_DETAIL_HREFS.todayForecast)}
              forecast={huddle.todayForecastApi}
            />
          )}
          {show(DashboardSection.Patients) && (
            <PatientsCard
              titleHref={huddleDetailHref('/dashboard/patients')}
              metrics={huddle.patientsApi.metrics}
              loading={huddle.patientsApi.loading}
              data={huddle.patientsApi.data}
            />
          )}
        </DashboardEditableRow>
      )}

      {activeDayTab === HuddleDay.Today && (
        <DashboardEditableRow
          editMode={editMode}
          className="dashboard-huddle-two-cards-row"
        >
          {show(DashboardSection.ProviderPerformanceBreakdown) && (
            <ProviderPerformanceBreakdownCard
              titleHref={huddleDetailHref(
                DASHBOARD_DETAIL_HREFS.providerPerformance,
              )}
              providerPerformance={huddle.providerPerformanceApi}
            />
          )}
          {show(DashboardSection.YesterdaysPerformance) && (
            <YesterdaysPerformanceCard
              titleHref={huddleDetailHref(
                DASHBOARD_DETAIL_HREFS.yesterdaysPerformance,
              )}
              performance={huddle.yesterdaysPerformanceApi}
            />
          )}
        </DashboardEditableRow>
      )}

      {activeDayTab === HuddleDay.Today &&
        show(DashboardSection.Leaderboard) && (
          <LeaderboardCard
            titleHref={huddleDetailHref(DASHBOARD_DETAIL_HREFS.leaderboard)}
            leaderboard={huddle.leaderboardApi}
          />
        )}

      {activeDayTab === HuddleDay.Today && show(DashboardSection.Events) && (
        <EventsCard events={huddle.eventsApi} />
      )}

      {activeDayTab === HuddleDay.Today &&
        show(DashboardSection.OverdueActions) && <OverdueActionsCard />}

      {activeDayTab === HuddleDay.Yesterday && (
        <>
          {show(DashboardSection.YesterdaysActuals) && (
            <YesterdayActualsCard
              titleHref={huddleDetailHref(
                DASHBOARD_DETAIL_HREFS.yesterdayActuals,
              )}
              actuals={huddle.yesterdayActualsApi}
            />
          )}

          {show(DashboardSection.ScheduleAdjustments) && (
            <ScheduleAdjustmentsCard
              titleHref={huddleDetailHref(
                DASHBOARD_DETAIL_HREFS.yesterdayScheduleAdjustments,
              )}
              scheduleAdjustments={huddle.scheduleAdjustmentsApi}
            />
          )}

          <DashboardEditableRow
            editMode={editMode}
            className="dashboard-huddle-two-cards-row"
          >
            {show(DashboardSection.PerformanceVsGoal) && (
              <PerformanceVsGoalCard
                titleHref={huddleDetailHref(
                  DASHBOARD_DETAIL_HREFS.yesterdayPerformance,
                )}
                performance={huddle.yesterdayPerformanceApi}
              />
            )}
            {show(DashboardSection.FollowUpsFromYesterday) && (
              <FollowUpsFromYesterdayCard />
            )}
          </DashboardEditableRow>

          {show(DashboardSection.ProviderActualsYesterday) && (
            <ProviderActualsYesterdayCard
              titleHref={huddleDetailHref(
                DASHBOARD_DETAIL_HREFS.yesterdayProviderActuals,
              )}
              providerActuals={huddle.yesterdayProviderActualsApi}
            />
          )}
        </>
      )}

      {activeDayTab === HuddleDay.Tomorrow && (
        <>
          {show(DashboardSection.TomorrowsForecast) && (
            <TomorrowsForecastCard
              titleHref={huddleDetailHref(
                DASHBOARD_DETAIL_HREFS.tomorrowForecast,
              )}
              forecast={huddle.tomorrowForecastApi}
            />
          )}

          {show(DashboardSection.ScheduleAtAGlance) && (
            <ScheduleAtAGlanceCard
              titleHref={huddleDetailHref(
                DASHBOARD_DETAIL_HREFS.tomorrowScheduleAtAGlance,
              )}
              scheduleAtAGlance={huddle.tomorrowScheduleAtAGlanceApi}
            />
          )}

          {show(DashboardSection.GoalsForTomorrow) && (
            <GoalsForTomorrowCard goals={huddle.tomorrowGoalsApi} />
          )}

          {show(DashboardSection.PreparationForTomorrow) && (
            <PreparationForTomorrowCard />
          )}
        </>
      )}
    </div>
  )
}

export default DashboardHuddle
